/**
 * Owner-scope: lets a single owner screen serve two callers without being
 * duplicated.
 *
 *   - A real owner renders the screen with no provider → calls hit the owner
 *     endpoints directly and in-app links point at /owner/...
 *   - An admin renders the same screen wrapped in <OwnerScopeProvider ownerId>
 *     → every API call is scoped with ?ownerId= (admin-as-owner) and links are
 *     rewritten to /admin/owner/<id>/...
 *
 * Screens get the scoped client + nav helpers from `useOwnerScope()`. This is
 * how the admin owner-twins under app/admin/owner/[id]/ reuse owner screens
 * with full parity and correct navigation.
 */
import React, { createContext, useContext, useMemo } from 'react';
import { api as rawApi, apiGet, apiPost, apiPatch, apiDelete } from '../api/client';

type ApiOptions = { method?: string; body?: unknown; headers?: Record<string, string> };

export interface ScopedApi {
  get<T = unknown>(path: string): Promise<T>;
  post<T = unknown>(path: string, body?: unknown): Promise<T>;
  patch<T = unknown>(path: string, body?: unknown): Promise<T>;
  put<T = unknown>(path: string, body?: unknown): Promise<T>;
  delete<T = unknown>(path: string): Promise<T>;
  /** Generic call (for the few screens that hit `api(path, { method })` directly). */
  api<T = unknown>(path: string, options?: ApiOptions): Promise<T>;
  /** Apply ?ownerId= scoping to a raw path (for fetch/export-from-URL callers). */
  scopePath(path: string): string;
}

export interface OwnerScope {
  /** The owner being acted on; null when the logged-in owner acts for themselves. */
  ownerId: string | null;
  /** Route prefix for in-app navigation. */
  basePath: string;
  api: ScopedApi;
  /** Rewrite an owner-absolute path ('/owner/...') into the active scope. */
  href(ownerPath: string): string;
}

const ownerSelfScope: OwnerScope = {
  ownerId: null,
  basePath: '/owner',
  href: (p) => p,
  api: {
    get: apiGet,
    post: apiPost,
    patch: apiPatch,
    delete: apiDelete,
    put: (p, b) => rawApi(p, { method: 'PUT', body: b }),
    api: (p, o) => rawApi(p, o),
    scopePath: (p) => p,
  },
};

function attach(path: string, ownerId: string): string {
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}ownerId=${encodeURIComponent(ownerId)}`;
}

const Ctx = createContext<OwnerScope>(ownerSelfScope);

export function OwnerScopeProvider({ ownerId, children }: { ownerId?: string | null; children: React.ReactNode }) {
  const value = useMemo<OwnerScope>(() => {
    if (!ownerId) return ownerSelfScope;
    const sp = (p: string) => attach(p, ownerId);
    return {
      ownerId,
      basePath: `/admin/owner/${ownerId}`,
      href: (p) => (p.startsWith('/owner') ? `/admin/owner/${ownerId}${p.slice('/owner'.length)}` : p),
      api: {
        get: (p) => apiGet(sp(p)),
        post: (p, b) => apiPost(sp(p), b),
        patch: (p, b) => apiPatch(sp(p), b),
        delete: (p) => apiDelete(sp(p)),
        put: (p, b) => rawApi(sp(p), { method: 'PUT', body: b }),
        api: (p, o) => rawApi(sp(p), o),
        scopePath: sp,
      },
    };
  }, [ownerId]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Returns the active owner scope (owner-self by default, admin-as-owner under a provider). */
export function useOwnerScope(): OwnerScope {
  return useContext(Ctx);
}
